import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';
import {
  supabase,
  handleOAuthRedirect,
  setAccessToken,
  isInIframe,
} from '../lib/supabaseClient';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function isOAuthCallback() {
  const h = window.location.hash;
  return h.includes('access_token') || h.includes('refresh_token');
}

export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const init = useRef(false);

  const applySession = useCallback((s) => {
    setSession(s);
    setUser(s?.user ?? null);
    setAccessToken(s?.access_token ?? null);
  }, []);

  useEffect(() => {
    if (init.current) return;
    init.current = true;

    const waitingForCallback = isOAuthCallback();

    // Listen for session handshake from popup auth
    const handleMessage = async (event) => {
      // Only accept messages from our own origin
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'SUPABASE_AUTH_SUCCESS') return;

      console.log('[Auth] Received session handshake from popup');
      const { access_token, refresh_token } = event.data;
      if (access_token && refresh_token) {
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token,
            refresh_token,
          });
          if (error) {
            console.error('[Auth] setSession error:', error);
          } else if (data.session) {
            console.log('[Auth] Session set from popup:', data.session.user?.email);
            applySession(data.session);
            setLoading(false);
          }
        } catch (err) {
          console.error('[Auth] Failed to set session from popup:', err);
        }
      }
    };
    window.addEventListener('message', handleMessage);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      console.log('[Auth] event:', event, s?.user?.email);
      applySession(s);
      if (['SIGNED_IN', 'TOKEN_REFRESHED', 'SIGNED_OUT', 'INITIAL_SESSION'].includes(event)) {
        setLoading(false);
      }
    });

    (async () => {
      try {
        const oauthSession = await handleOAuthRedirect();
        if (oauthSession) { applySession(oauthSession); setLoading(false); return; }
        const { data: { session: existing } } = await supabase.auth.getSession();
        if (existing) { applySession(existing); setLoading(false); return; }
        if (!waitingForCallback) setLoading(false);
      } catch (err) {
        console.error('[Auth] init error:', err);
        setLoading(false);
      }
    })();

    const t = setTimeout(() => setLoading(false), 5000);
    return () => {
      subscription?.unsubscribe();
      window.removeEventListener('message', handleMessage);
      clearTimeout(t);
    };
  }, [applySession]);

  // ── Standard redirect auth (not in iframe) ──
  const signInWithRedirect = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
        queryParams: { access_type: 'offline', prompt: 'consent' },
      },
    });
    if (error) throw error;
  };

  // ── Popup auth (for iframe context) ──
  const signInWithPopup = () => {
    return new Promise(async (resolve, reject) => {
      // Step 1: Open popup IMMEDIATELY (synchronous with user gesture)
      const width = 500, height = 650;
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      const popup = window.open(
        `${window.location.origin}/auth-loading`,
        'cmg-auth',
        `width=${width},height=${height},left=${left},top=${top},popup=true`
      );

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for ops.cmg.systems and try again.'));
        return;
      }

      // Step 2: Get Google auth URL from Supabase (async is safe, popup already open)
      try {
        const redirectUrl = `${window.location.origin}/auth-callback`;
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: {
            redirectTo: redirectUrl,
            queryParams: { access_type: 'offline', prompt: 'consent' },
            skipBrowserRedirect: true,
          },
        });

        if (error) {
          popup.close();
          reject(new Error(`Supabase auth error: ${error.message}`));
          return;
        }

        if (!data?.url) {
          popup.close();
          reject(new Error('No auth URL returned. Check Supabase Google provider config.'));
          return;
        }

        // Step 3: Redirect the already-open popup to Google
        popup.location.href = data.url;

      } catch (err) {
        try { popup.close(); } catch {}
        reject(new Error(`Auth setup failed: ${err.message}`));
        return;
      }

      // Step 4: Poll for popup close OR postMessage session handshake
      let resolved = false;

      // Listen for postMessage from popup (primary handshake)
      const onMessage = async (event) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type !== 'SUPABASE_AUTH_SUCCESS') return;
        if (resolved) return;
        resolved = true;
        window.removeEventListener('message', onMessage);
        clearInterval(interval);

        const { access_token, refresh_token } = event.data;
        if (access_token && refresh_token) {
          const { data: sd } = await supabase.auth.setSession({ access_token, refresh_token });
          if (sd?.session) {
            applySession(sd.session);
            setLoading(false);
          }
        }
        resolve();
      };
      window.addEventListener('message', onMessage);

      // Fallback: poll for popup close
      const interval = setInterval(async () => {
        if (resolved) { clearInterval(interval); return; }
        try {
          if (popup.closed) {
            clearInterval(interval);
            if (resolved) return;
            // Give postMessage a moment
            await new Promise(r => setTimeout(r, 800));
            if (resolved) return;
            // Fallback: check session directly
            const { data: { session: s } } = await supabase.auth.getSession();
            if (s) {
              resolved = true;
              applySession(s);
              setLoading(false);
              resolve();
            } else {
              await new Promise(r => setTimeout(r, 1500));
              if (resolved) return;
              const { data: { session: retry } } = await supabase.auth.getSession();
              if (retry) {
                resolved = true;
                applySession(retry);
                setLoading(false);
                resolve();
              } else {
                window.removeEventListener('message', onMessage);
                reject(new Error('Authentication cancelled or session not found'));
              }
            }
          }
        } catch {}
      }, 500);

      setTimeout(() => {
        clearInterval(interval);
        window.removeEventListener('message', onMessage);
        try { if (!popup.closed) popup.close(); } catch {}
      }, 180000);
    });
  };

  // ── Main sign-in method (auto-detects iframe) ──
  const signInWithGoogle = async () => {
    if (isInIframe()) {
      return signInWithPopup();
    }
    return signInWithRedirect();
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    applySession(null);
  };

  return (
    <AuthContext.Provider
      value={{
        session, user, loading,
        signInWithGoogle, signOut,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
